import React, { useState, useEffect } from "react";
import axios from "axios";
import { Smartphone, CheckCircle2, Trophy } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import { isHapticEnabled, setHapticEnabled, isHapticSupported, triggerHaptic } from "@/utils/haptic";

/** Mobile-app preferences panel — currently surfaces a single toggle for
 *  haptic feedback. Built to live inside the "Още" mobile tab.
 */
export default function AppPreferencesPanel() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { API, user, setUser } = useAuth();
  const bg = language === "bg";
  const supported = isHapticSupported();
  const [enabled, setEnabledState] = useState(() => isHapticEnabled());
  // iter614 — Hero Score Switch
  const initialHero = user?.preferences?.hero_score || "hi";
  const [heroScore, setHeroScore] = useState(initialHero);

  useEffect(() => {
    setHeroScore(user?.preferences?.hero_score || "hi");
  }, [user]);

  const onToggle = () => {
    const next = !enabled;
    setHapticEnabled(next);
    setEnabledState(next);
    if (next) {
      triggerHaptic([12, 40, 12]);
    }
  };

  const onHeroChange = async (val) => {
    setHeroScore(val);
    try {
      await axios.patch(`${API}/auth/me`, { preferences: { hero_score: val } });
      if (setUser && user) {
        setUser({ ...user, preferences: { ...(user.preferences || {}), hero_score: val } });
      }
    } catch (e) {
      // Revert on failure
      setHeroScore(initialHero);
    }
  };

  return (
    <div
      className={`rounded-2xl border p-4 ${isDark ? "bg-card border-border" : "bg-white card-elevated border-0"}`}
      data-testid="app-preferences-panel"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
          <Smartphone className="w-4 h-4 text-white" />
        </div>
        <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
          {bg ? "Настройки на приложението" : "App preferences"}
        </h3>
      </div>

      <div className="space-y-3">
        <label
          className={`flex items-start justify-between gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
            isDark
              ? "border-[hsl(223,47%,20%)] hover:bg-[hsl(223,47%,14%)]"
              : "border-gray-100 hover:bg-slate-50"
          }`}
        >
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {bg ? "Тактилна обратна връзка" : "Haptic feedback"}
            </p>
            <p className={`text-[11px] mt-0.5 leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg
                ? "Лек вибрационен сигнал при отваряне на секции (само Android, не iOS)."
                : "Short vibration tick when opening sections (Android only — iOS doesn't expose vibration to the web)."}
            </p>
            {!supported && (
              <p className={`text-[11px] mt-1 italic ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                {bg
                  ? "Това устройство не поддържа вибрация в браузъра."
                  : "This device doesn't support browser vibration."}
              </p>
            )}
          </div>
          <input
            type="checkbox"
            checked={enabled}
            onChange={onToggle}
            disabled={!supported}
            className="sr-only peer"
            data-testid="haptic-toggle-input"
          />
          <span
            onClick={(e) => { e.preventDefault(); if (supported) onToggle(); }}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 mt-0.5 rounded-full transition-colors ${
              !supported ? "bg-gray-200 opacity-50 cursor-not-allowed" :
              enabled ? "bg-gradient-to-r from-[#6366F1] to-[#8B5CF6]" :
              isDark ? "bg-[hsl(223,47%,22%)]" : "bg-gray-200"
            }`}
            data-testid="haptic-toggle-switch"
            role="switch"
            aria-checked={enabled}
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ml-0.5 ${
                enabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </span>
        </label>

        {enabled && supported && (
          <div className="flex items-center gap-1.5 text-[11px] text-emerald-600">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>{bg ? "Включено — усещате го при отваряне на секции." : "Enabled — you'll feel it when opening sections."}</span>
          </div>
        )}

        {/* iter614 — Hero Score Switch */}
        <div
          className={`p-3 rounded-xl border ${
            isDark ? "border-[hsl(223,47%,20%)] bg-[hsl(223,47%,14%)]/40" : "border-gray-100 bg-slate-50"
          }`}
          data-testid="hero-score-switch"
        >
          <div className="flex items-start gap-2 mb-2">
            <Trophy className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? "Главен скор на Dashboard" : "Dashboard hero score"}
              </p>
              <p className={`text-[11px] mt-0.5 leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg
                  ? "Кой скор да виждаш най-горе — HI (колко данни сме събрали) или Twin Score (как се представят 7-те близнака)."
                  : "Which score to show at the top — HI (data completeness) or Twin Score (how the 7 twins perform)."}
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            {[
              { v: "hi", title: bg ? "HI Score (data, 0-1000)" : "HI Score (data, 0-1000)", desc: bg ? "Стимулира пълнене на профила" : "Encourages profile completion" },
              { v: "twin", title: bg ? "Twin Score (outcome, 0-100)" : "Twin Score (outcome, 0-100)", desc: bg ? "За напреднали — реално здраве" : "For advanced — actual health" },
              { v: "both", title: bg ? "И двата заедно" : "Both side-by-side", desc: bg ? "Пълна картина" : "Full picture" },
            ].map((opt) => (
              <label
                key={opt.v}
                className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                  heroScore === opt.v
                    ? (isDark ? "bg-amber-500/15 border border-amber-500/30" : "bg-amber-50 border border-amber-300")
                    : (isDark ? "hover:bg-[hsl(223,47%,18%)] border border-transparent" : "hover:bg-white border border-transparent")
                }`}
                data-testid={`hero-score-option-${opt.v}`}
              >
                <input
                  type="radio"
                  name="hero-score"
                  value={opt.v}
                  checked={heroScore === opt.v}
                  onChange={() => onHeroChange(opt.v)}
                  className="mt-0.5 accent-amber-500"
                  data-testid={`hero-score-input-${opt.v}`}
                />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{opt.title}</p>
                  <p className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>{opt.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
